import React, { useEffect } from "react";
import { useTable, usePagination } from "react-table";
import createPagination from "../Hooks/usePagination";
import { MdCompareArrows } from "react-icons/md";
import "./advancedTable.css";

let reload = false;
let page = 1;

const Table = ({
    columns,
    data,
    updatePagination,
    paginationState,
    pageCount: controlledPageCount,
    sortable,
    hidePagination,
    updateLoaded,
    total,
}) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        gotoPage,
        pageCount,
        canNextPage,
        canPreviousPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            manualPagination: true,
            pageCount: controlledPageCount,
            initialState: {
                pageIndex: 0,
            },
        },
        usePagination
    );

    useEffect(() => {
        if (reload) {
            updatePagination((prev) => ({
                ...prev,
                page: pageIndex,
            }));

            updateLoaded(false);
            reload = false;
        }
    }, [updatePagination, updateLoaded, pageIndex, pageSize]);

    const { pagination } = createPagination({
        numberOfButtons: 2,
        currentPage: paginationState ? paginationState.page : pageIndex + 1,
        totalPages: paginationState
            ? paginationState.totalPages
                ? paginationState.totalPages
                : controlledPageCount
            : controlledPageCount,
    });

    return (
        <div className="table-wrapper block max-w-full overflow-x-auto rounded-2xl shadow-2xl border border-slate-200/50">
            <table className="bg-white w-full table-fixed" style={{minWidth: 'max-content'}} {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th
                                    className="p-6 bg-black text-white font-bold text-sm tracking-wide border-r border-gray-700/30 last:border-r-0 whitespace-nowrap"
                                    style={{
                                        width: column.width || 'auto',
                                        minWidth: column.width || '150px',
                                        maxWidth: 'none'
                                    }}
                                    {...column.getHeaderProps()}
                                >
                                    <div
                                        className={`flex items-center justify-center`}
                                    >
                                        <div className="whitespace-nowrap">
                                            {column.render("Header")}
                                        </div>
                                        {sortable && (
                                            <div className="cursor-pointer ml-2 hover:text-gray-300 transition-colors duration-200">
                                                {sortable.includes(
                                                    column.id
                                                ) && (
                                                    <MdCompareArrows
                                                        className="text-lg"
                                                        onClick={() => {
                                                            updatePagination(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    by: column.id,
                                                                    dir:
                                                                        prev.dir ===
                                                                        "asc"
                                                                            ? "desc"
                                                                            : "asc",
                                                                    page: 1,
                                                                })
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, index) => {
                        prepareRow(row);
                        return (
                            <tr
                                className={`
                                    ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} 
                                    hover:bg-blue-50 transition-all duration-200
                                    ${row.original.status === "cancelled"
                                        ? "bg-red-500 text-white hover:bg-red-600"
                                        : row.original.status === "rejected"
                                        ? "bg-red-500 text-white hover:bg-red-600"
                                        : row.original.status === "pending"
                                        ? "bg-yellow-400 text-slate-800 hover:bg-yellow-500"
                                        : row.original.status === "completed"
                                        ? "bg-green-400 text-slate-800 hover:bg-green-500"
                                        : row.original.status === "approved"
                                        ? "bg-green-400 text-slate-800 hover:bg-green-500"
                                        : row.original.status === "suspended"
                                        ? "bg-red-500 text-white hover:bg-red-600"
                                        : row.original.status === "void"
                                        ? "bg-purple-500 text-white hover:bg-purple-600"
                                        : row.original.calculatePoints === "void"
                                        ? "bg-red-500 text-white hover:bg-red-600"
                                        : ""
                                    }
                                `}
                                {...row.getRowProps()}
                            >
                                {row.cells.map((cell, cellIndex) => {
                                    return (
                                        <td
                                            className={`text-center p-4 text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis ${
                                                cellIndex === 0 ? 'pl-6' : ''
                                            } ${
                                                cellIndex === row.cells.length - 1 ? 'pr-6' : ''
                                            }`}
                                            style={{
                                                width: cell.column.width || 'auto',
                                                minWidth: cell.column.width || '150px',
                                                maxWidth: cell.column.width || 'none'
                                            }}
                                            {...cell.getCellProps()}
                                        >
                                            {cell.render("Cell")}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
                {total && (
                    <tfoot>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                <th className="p-6 bg-yellow-400 text-slate-800 font-bold text-sm tracking-wide min-w-max whitespace-nowrap">
                                    <div
                                        className={`flex items-center justify-center`}
                                    >
                                        <div className="whitespace-nowrap">
                                            Total
                                        </div>
                                    </div>
                                </th>
                                <th className="p-6 bg-yellow-400 text-slate-800 font-bold text-sm tracking-wide min-w-max whitespace-nowrap">
                                    <div
                                        className={`flex items-center justify-center`}
                                    >
                                        <div className="whitespace-nowrap">
                                            RM {total[0]}
                                        </div>
                                    </div>
                                </th>
                                <th className="p-6 bg-yellow-400 text-slate-800 font-bold text-sm tracking-wide min-w-max whitespace-nowrap">
                                    <div
                                        className={`flex items-center justify-center`}
                                    >
                                        <div className="whitespace-nowrap">
                                            RM {total[1]}
                                        </div>
                                    </div>
                                </th>
                            </tr>
                        ))}
                    </tfoot>
                )}
            </table>
            {!hidePagination && (
                <div className="p-6 bg-slate-50 border-t border-slate-200/50">
                    <ul className="flex justify-center items-center gap-2">
                        <li
                            className="px-4 py-2 cursor-pointer bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-medium rounded-lg shadow-sm border border-slate-200 transition-all duration-200 hover:shadow-md hover:scale-105"
                            onClick={() => {
                                page = 1;
                                gotoPage(1);
                                reload = true;
                            }}
                            disabled={!canPreviousPage}
                        >
                            First
                        </li>
                        <li
                            className="px-4 py-2 cursor-pointer bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-medium rounded-lg shadow-sm border border-slate-200 transition-all duration-200 hover:shadow-md hover:scale-105"
                            onClick={() => {
                                gotoPage(pagination[0]);
                                page = page - 1;
                                reload = true;
                            }}
                        >
                            Previous
                        </li>
                        {pagination.map((list) => (
                            <li
                                className={`px-4 py-2 cursor-pointer font-medium rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md hover:scale-105 ${
                                    list === page
                                        ? "bg-blue-600 text-white border-blue-500 shadow-lg"
                                        : "bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-600 border-slate-200"
                                }`}
                                key={list}
                                onClick={() => {
                                    gotoPage(list);
                                    reload = true;
                                    page = list;
                                }}
                            >
                                {list}
                            </li>
                        ))}
                        <li
                            className="px-4 py-2 cursor-pointer bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-medium rounded-lg shadow-sm border border-slate-200 transition-all duration-200 hover:shadow-md hover:scale-105"
                            onClick={() => {
                                gotoPage(
                                    page === 1 ? pagination[1] : pagination[2]
                                );
                                page = page + 1;
                                reload = true;
                            }}
                        >
                            Next
                        </li>
                        <li
                            className="px-4 py-2 cursor-pointer bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-medium rounded-lg shadow-sm border border-slate-200 transition-all duration-200 hover:shadow-md hover:scale-105"
                            onClick={() => {
                                gotoPage(pageCount - 1);
                                page = pageCount - 1;
                                reload = true;
                            }}
                            disabled={!canNextPage}
                        >
                            Last
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Table;
